/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { isAddNewRfFeedSelector, rfFeedListSelector, rfFeedIndexesSelector } from "../../../store/api/deviceConfig/selectors";
import { IRfFeed } from "../../../store/api/deviceConfig/types";
import { getNextRfFeedIndex, prepareRfFeedLatestPayload } from "../helpers";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { OPERATIONTYPES } from "../../../globals/constants";
import { useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenCustomDailog, setOpenToast } from "../../../store/helpers/helperReducers";
import { setRfFeedList } from "../../../store/api/deviceConfig/reducers";
import { errorMessage } from "../../../utils/helpers";

interface IPayload {
  [key: string]: string;
}

export const useRfFeedData = (selectedRfFeed?: IRfFeed) => {
  const dispatch = useStoreDispatch();
  const [RfFeedData, setRfFeedData] = useState(selectedRfFeed);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [anchorElRfFeed, setAnchorElRfFeed] = useState<null | HTMLElement>(null);
  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  let RfFeedList: IRfFeed[] = useSelector(rfFeedListSelector);
  const isAddNewRfFeed = useSelector(isAddNewRfFeedSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const RfFeedIndexes = useSelector(rfFeedIndexesSelector);

  const nextRfFeedIndex = getNextRfFeedIndex(RfFeedIndexes, 10);

  useEffect(() => {
    if (isAddNewRfFeed) {
      setRfFeedData({
        ...selectedRfFeed,
        index: nextRfFeedIndex + "",
      });
    } else {
      setRfFeedData(selectedRfFeed);
    }
  }, [isAddNewRfFeed, nextRfFeedIndex, selectedRfFeed]);

  const onblureHandler = (event: any) => {
    if (isAddNewRfFeed && RfFeedIndexes?.length > 0) {
      if (RfFeedIndexes?.includes(RfFeedData?.index)) {
        setErrMsg({ ...errMsg, index: `Index ${RfFeedData?.index} is already in use.` });
        return;
      }
    }
    if (nextRfFeedIndex !== RfFeedData?.index) {
      setRfFeedData({ ...RfFeedData });
    }
  };

  const submitHandler = async () => {
    if (!!RfFeedData) {
      if (Object.keys(RfFeedData).length <= 0) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please fill the required fields!" }));
        return;
      }
      //onblureHandler();
      // const { errorMessages, isValid } = validate(RfFeedData);
      // setErrMsg({ ...errMsg, ...errorMessages });

      const operationType = isAddNewRfFeed ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
      const RfFeedPayload =
        !!RfFeedData &&
        !!selectedDevice?.objectKey &&
        prepareRfFeedLatestPayload(RfFeedData, selectedRfFeed, selectedDevice?.objectKey, operationType);
      if (!!RfFeedData && operationType !== OPERATIONTYPES.UPDATE) {
        RfFeedList = [RfFeedData, ...RfFeedList];
      }
      try {
        let result: any;
        dispatch(setRfFeedList(RfFeedList));
        dispatch(setOpenCustomDailog(false));
        setAnchorElRfFeed(null);

        result = await setDeviceConfig(RfFeedPayload);
        if (result?.error || configStatus?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been submited`,
            })
          );
        }
      } catch (error: any) {
        if (operationType !== OPERATIONTYPES.UPDATE) {
          let lastAdded = RfFeedList.filter((item) => item.index === RfFeedData.index)[0];
          lastAdded = { ...lastAdded };
          const updateToRfFeedList = RfFeedList.filter((item) => item.index !== RfFeedData.index);
          dispatch(setRfFeedList([lastAdded, ...updateToRfFeedList]));
          setTimeout(() => {
            const removedFailed = RfFeedList.filter((item) => item.index !== RfFeedData.index);
            dispatch(setRfFeedList(removedFailed));
          }, 5000);
        }
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    RfFeedData,
    setRfFeedData,
    isAddNewRfFeed,
    submitHandler,
    onblureHandler,
    errMsg,
    setErrMsg,
    anchorElRfFeed,
    setAnchorElRfFeed,
  };
};
